import React, {useContext, useState, useEffect} from 'react';
import {CartContext} from '../context/CartContext';
import {
    Button, Card, CardContent, CardMedia, Container, Grid, IconButton, TextField, Typography, Box,
    RadioGroup, FormControlLabel, Radio, FormControl, FormLabel, Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

function Cart() {
    const {cartItems, removeFromCart, updateQuantity} = useContext(CartContext);
    const [deliveryMethod, setDeliveryMethod] = useState('pickup');
    const [storeInfo, setStoreInfo] = useState(null);
    const [isFormValid, setIsFormValid] = useState(false);
    const [shippingAddress, setShippingAddress] = useState({
        firstName: '',
        lastName: '',
        street: '',
        city: '',
        state: '',
        zip: '',
        country: 'US',
        phone: '',
        email: ''
    });

    useEffect(() => {
        const hasPhysicalProduct = cartItems.some(item => item.itemData.productType !== 'DIGITAL');

        const isEmailValid = /\S+@\S+\.\S+/.test(shippingAddress.email);

        let isShippingAddressValid = true;
        if (hasPhysicalProduct && deliveryMethod === 'shipping') {
            isShippingAddressValid =
                shippingAddress.firstName.trim() !== '' &&
                shippingAddress.lastName.trim() !== '' &&
                shippingAddress.street.trim() !== '' &&
                shippingAddress.city.trim() !== '' &&
                shippingAddress.state.trim() !== '' &&
                shippingAddress.zip.trim() !== '' &&
                shippingAddress.phone.trim() !== '';
        }

        setIsFormValid(isEmailValid && isShippingAddressValid);
    }, [cartItems, deliveryMethod, shippingAddress]);

    useEffect(() => {
        fetch('/api/store-info')
            .then(res => res.json())
            .then(data => setStoreInfo(data))
            .catch(err => console.error("Failed to fetch store info:", err));
    }, []);

    const hasPhysicalProduct = cartItems.some(item => item.itemData.productType !== 'DIGITAL');
    const countOfPhysicalItems = cartItems.filter(item => item.itemData.productType !== 'DIGITAL').length;

    const handleAddressChange = (e) => {
        const {name, value} = e.target;
        setShippingAddress({...shippingAddress, [name]: value});
    };

    const getTotalPrice = () => {
        const itemsTotal = cartItems.reduce((total, item) => {
            const price = item.itemData.variations[0].itemVariationData.priceMoney.amount / 100;
            return total + price * item.quantity;
        }, 0);
        if (hasPhysicalProduct && deliveryMethod === 'shipping' && storeInfo) {
            return itemsTotal + (storeInfo.shippingCost * countOfPhysicalItems);
        }
        return itemsTotal;
    };

    const handlePurchase = () => {
        const order = {
            items: cartItems.map(item => ({
                id: item.id,
                type: item.itemData.productType,
            })),
            lineItems: cartItems.map(item => ({
                quantity: item.quantity.toString(),
                catalogObjectId: item.itemData.variations[0].id,
            })),
            deliveryMethod,
            shippingAddress,
            shippingCost: storeInfo ? storeInfo.shippingCost : 0,
        };

        fetch('/api/checkout', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({order}),
        })
            .then(res => res.json())
            .then(data => {
                if (data.checkoutUrl && data.checkoutUrl.orderId) {
                    localStorage.setItem('pendingOrderId', data.checkoutUrl.orderId);
                }
                window.location.href = data.checkoutUrl.url;
            })
            .catch(err => {
                console.error('Error creating checkout link:', err);
            });
    };

    if (cartItems.length === 0) {
        return (
            <Container>
                <Typography variant="h6">Your cart is empty</Typography>
            </Container>
        );
    }

    return (
        <Container>
            <Typography variant="h4" gutterBottom>Shopping Cart</Typography>

            <Grid container spacing={2}>
                {cartItems.map(item => (
                    <Grid item key={item.id} xs={12}>
                        <Card>
                            <Grid container>
                                <Grid item xs={12} sm={3}>
                                    <CardMedia
                                        component="img"
                                        image={item.itemData.imageUrl}
                                        alt={item.itemData.name}
                                        sx={{maxWidth: '155px', height: 'auto', objectFit: 'cover'}}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={9}>
                                    <CardContent>
                                        <Typography variant="h6">{item.itemData.name}</Typography>

                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {new Intl.NumberFormat('en-US', {
                                                style: 'currency',
                                                currency: item.itemData.variations[0].itemVariationData.priceMoney.currency,
                                            }).format(item.itemData.variations[0].itemVariationData.priceMoney.amount / 100)}
                                        </Typography>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <TextField
                                                label="Quantity"
                                                type="number"
                                                value={item.quantity}
                                                onChange={(e) => updateQuantity(item.id, e.target.value)}
                                                inputProps={{min: 1}}
                                                size="small"
                                                sx={{ width: '100px' }}
                                            />

                                            <IconButton
                                                onClick={() => removeFromCart(item.id)}
                                                aria-label="delete"
                                                color="error"
                                            >
                                                <DeleteIcon/>
                                            </IconButton>
                                        </Box>
                                    </CardContent>
                                </Grid>
                            </Grid>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <TextField
                label="Email Address"
                name="email"
                value={shippingAddress.email}
                onChange={handleAddressChange}
                fullWidth
                required
                error={!/\S+@\S+\.\S+/.test(shippingAddress.email)}
                helperText={!/\S+@\S+\.\S+/.test(shippingAddress.email) ? 'Please enter a valid email address.' : ''}
                margin="normal"
                sx={{ mt: 2 }}
            />

            {hasPhysicalProduct && (
                <FormControl component="fieldset" sx={{ mt: 2 }}>
                    <FormLabel component="legend">Delivery Method</FormLabel>
                    <RadioGroup
                        row
                        name="deliveryMethod"
                        value={deliveryMethod}
                        onChange={(e) => setDeliveryMethod(e.target.value)}
                    >
                        <FormControlLabel value="pickup" control={<Radio />} label="Pickup" />
                        <FormControlLabel value="shipping" control={<Radio />} label="Ship to me" />
                    </RadioGroup>
                </FormControl>
            )}

            {hasPhysicalProduct && deliveryMethod === 'shipping' && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="h6">Shipping Address</Typography>

                    <TextField label="First Name" name="firstName" value={shippingAddress.firstName} onChange={handleAddressChange} fullWidth margin="normal" required />
                    <TextField label="Last Name" name="lastName" value={shippingAddress.lastName} onChange={handleAddressChange} fullWidth margin="normal" required />
                    <TextField label="Street" name="street" value={shippingAddress.street} onChange={handleAddressChange} fullWidth margin="normal" required />
                    <TextField label="City" name="city" value={shippingAddress.city} onChange={handleAddressChange} fullWidth margin="normal" required />
                    <TextField label="State" name="state" value={shippingAddress.state} onChange={handleAddressChange} fullWidth margin="normal" required />
                    <TextField label="ZIP Code" name="zip" value={shippingAddress.zip} onChange={handleAddressChange} fullWidth margin="normal" required />
                    <TextField label="Phone Number" name="phone" value={shippingAddress.phone} onChange={handleAddressChange} fullWidth margin="normal" required />
                </Box>
            )}

            {hasPhysicalProduct && deliveryMethod === 'pickup' && storeInfo && (
                <Typography variant="body1" sx={{ mt: 2 }}>
                    Pickup at: {storeInfo.address}
                </Typography>
            )}

            <Grid container spacing={2} margin={2} alignItems="center" justifyContent="center">
                <Grid item xs={12}>
                    <Typography variant="h5" align="right" sx={{ mt: 2 }}>
                        Total: {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD'
                    }).format(getTotalPrice())}
                    </Typography>
                </Grid>
            </Grid>

            <Grid container spacing={2} margin={2} alignItems="center" justifyContent="center">
                <Grid item xs={12}>
                    <Tooltip title={!isFormValid ? "Please fill in all required fields." : ""}>
                        <span>
                            <Button variant="contained" color="primary" onClick={handlePurchase} fullWidth disabled={!isFormValid}>
                                Purchase
                            </Button>
                        </span>
                    </Tooltip>
                </Grid>
            </Grid>
        </Container>
    );
}

export default Cart;
